import { APP_ROUTES, INTERNAL_API_ENDPOINTS, SESSION_STORAGE_KEYS } from "@src/constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useTimerContext } from "@src/context/TimerContext";
import ClientLogsService from "@src/services/client-logs.service";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import { useEffect } from "react";

/**
 * Module-level flag that allows programmatic navigations (e.g. idle-modal
 * redirects) to bypass the browser's "Leave site?" confirmation dialog.
 * Set this to `true` right before calling `window.location.href = …` and
 * the next `beforeunload` will be silently allowed through.
 */
let bypassBeforeUnload = false;

export const setBypassBeforeUnload = (value: boolean) => {
  bypassBeforeUnload = value;
};

interface PatientInformationExitOptions {
  userUuid?: string;
  isTimerRunning: boolean;
  stopTimer: () => void;
  source: string;
  logMessage: string;
  shouldSetFindProviderRedirect?: boolean;
  unloadRedirectPath?: string;
}

export const handlePatientInformationExit = ({
  userUuid,
  isTimerRunning,
  stopTimer,
  source,
  logMessage,
  shouldSetFindProviderRedirect = true,
  unloadRedirectPath = APP_ROUTES.FIND_PROVIDER,
}: PatientInformationExitOptions) => {
  ClientLogsService.logWebAction(userUuid || "unknown", source, logMessage, undefined, {});

  if (userUuid) {
    const encodedUserUuid = encodeURIComponent(userUuid);
    const url = `${INTERNAL_API_ENDPOINTS.UNFREEZE_APPOINTMENTS}?userUuid=${encodedUserUuid}`;
    navigator.sendBeacon(url);
  } else {
    ClientLogsService.logError(
      "unknown",
      source,
      "No userUuid found in application context data to unfreeze appointment.",
    );
  }

  if (isTimerRunning) {
    stopTimer();
  }
  if (shouldSetFindProviderRedirect) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.PATIENT_INFO_UNLOAD_REDIRECT, unloadRedirectPath);
  }
};

/**
 * Returns true when the current page load is a same-tab browser refresh
 * (F5 / Ctrl-R / hard-reload).  Uses the Navigation Timing API's `type`
 * field, which is the most reliable cross-browser signal available during
 * page initialisation.  Falls back to false so cleanup always runs when the
 * API is unavailable.
 */
export const isBrowserRefresh = (): boolean => {
  try {
    const entries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    return entries[0]?.type === "reload";
  } catch {
    return false;
  }
};

// Reusable hook for adding event listeners
const useUnloadHandler = (
  routesConfig: Record<string, { showConfirmation: boolean; onUnload: () => void }>,
  onBeforeUnload?: () => void,
) => {
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    const routeEntry = Object.entries(routesConfig).find(([basePath]) => {
      return pathname === basePath || pathname.startsWith(`${basePath}/`);
    });
    const routeConfig = routeEntry?.[1];
    if (!routeConfig) {
      return; // No configuration for the current route, skip adding listeners
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (bypassBeforeUnload) {
        bypassBeforeUnload = false; // Reset so future navigations are not bypassed
        return;
      }
      if (routeConfig.showConfirmation) {
        event.preventDefault();
        onBeforeUnload?.(); // Optional callback for beforeunload
      }
    };

    const handleUnload = () => {
      routeConfig.onUnload(); // Execute the unload logic for the current route
    };

    if (routeConfig.showConfirmation) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    window.addEventListener("unload", handleUnload);

    return () => {
      if (routeConfig.showConfirmation) {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      }
      window.removeEventListener("unload", handleUnload);
    };
  }, [pathname, routesConfig, onBeforeUnload]);
};

// Main hook for managing navigation blockers
export const useAppNavigationBlocker = () => {
  const { data, clearSession } = useApplicationContext();
  const { isTimerRunning, stopTimer } = useTimerContext();
  const { FEATURE_SCHEDULING_CONSOLIDATION } = useFeatureFlags();
  const unloadRedirectPath = FEATURE_SCHEDULING_CONSOLIDATION
    ? APP_ROUTES.FIND_PROVIDER
    : APP_ROUTES.PROVIDER_PROFILE;

  // Shared logic for cleaning up the session
  const cleanUpSession = () => {
    if (isTimerRunning) {
      stopTimer();
    }
    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "useAppNavigationBlocker.cleanUpSession",
      "Cleaning up session data.",
      undefined,
      {},
    );
    clearSession();
  };

  // Route-specific configuration
  const routesConfig = {
    [APP_ROUTES.PATIENT_INFORMATION]: {
      showConfirmation: true,
      onUnload: () => {
        handlePatientInformationExit({
          userUuid: data.userUuid,
          isTimerRunning,
          stopTimer,
          source: "useAppNavigationBlocker.routesConfig",
          logMessage:
            "User refreshed or closed the tab on /patient-information. Unfreezing appointment and redirecting to /find-provider if tab is refreshed.",
          unloadRedirectPath,
        });
      },
    },
    [APP_ROUTES.CONTACT_US]: {
      showConfirmation: false,
      onUnload: () => {
        ClientLogsService.logWebAction(
          data.userUuid || "unknown",
          "useAppNavigationBlocker.routesConfig",
          "User refreshed or closed the tab on /contact-us. Cleaning up session.",
          undefined,
          {},
        );
        cleanUpSession();
      },
    },
    [APP_ROUTES.CONTACT]: {
      showConfirmation: false,
      onUnload: () => {
        ClientLogsService.logWebAction(
          data.userUuid || "unknown",
          "useAppNavigationBlocker.routesConfig",
          "User refreshed or closed the tab on /contact. Cleaning up session.",
          undefined,
          {},
        );
        cleanUpSession();
      },
    },
    [APP_ROUTES.APPOINTMENT_CONFIRMED]: {
      showConfirmation: false,
      onUnload: () => {
        ClientLogsService.logWebAction(
          data.userUuid || "unknown",
          "useAppNavigationBlocker.routesConfig",
          "User refreshed or closed the tab on /appointment-confirmed. Cleaning up session.",
          undefined,
          {},
        );
        cleanUpSession();
      },
    },
  };

  // Use the reusable unload handler
  useUnloadHandler(routesConfig, () => {
    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "useAppNavigationBlocker.useUnloadHandler",
      "User is attempting to reload or close the tab. Triggering beforeunload logic.",
      undefined,
      {},
    );
  });
};
